import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Divider from 'shared/components/divider/divider';

import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';
import WanderingDetectionTimeFrameConfiguration from 'features/residents/models/configurations/wandering-detection-time-frame-configuration';
import WanderingBoundaryCard from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/wandering-detection-scenario-configuration/wandering-boundary-card/wandering-boundary-card';
import WanderingBoundaryLinkModal from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/wandering-detection-scenario-configuration/wandering-boundary-link-modal/wandering-boundary-link-modal';
import { useWanderingDetectionSettings } from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/wandering-detection-scenario-configuration/wandering-detection-settings/hooks';
import styles from './wandering-detection-settings.module.scss';

type Props = {
  wanderingDetectionConfiguration: WanderingDetectionConfiguration;
  activeAllDay: boolean;
  index: number;
  valuesChanged: (newTimeFrameConfigurations: WanderingDetectionTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
  updateResidentSettingEditFormScenario: () => void;
  selectedWanderingBoundaryIds?: string[];
};

const WanderingDetectionSettings = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const {
    linkWanderingBoundaryModalIsOpen,
    openLinkWanderingBoundaryModal,
    closeLinkWanderingBoundaryModal,
    onWanderingBoundaryLinkClicked,
    onUnlinkWanderingBoundaryClicked,
    wanderingBoundaries,
    viewingMode,
  } = useWanderingDetectionSettings({
    wanderingDetectionConfiguration: props.wanderingDetectionConfiguration,
    activeAllDay: props.activeAllDay,
    index: props.index,
    valuesChanged: props.valuesChanged,
  });

  const hasBoundaries = props.selectedWanderingBoundaryIds && props.selectedWanderingBoundaryIds.length > 0;
  const isEditing = viewingMode === 'editing';

  if (!isEditing && !hasBoundaries) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {isEditing && (
        <Button
          startIcon={<AddCircleOutlineRounded color="primary" />}
          variant="text"
          onClick={openLinkWanderingBoundaryModal}
          onFocus={(event) => event.stopPropagation()}
        >
          {t('scenarioConfiguration.linkWanderingBoundary')}
        </Button>
      )}
      {wanderingBoundaries &&
        props.selectedWanderingBoundaryIds?.map((boundaryId) => {
          const wanderingBoundary = wanderingBoundaries.find((x) => x.id === boundaryId);
          return (
            <div key={boundaryId} className={styles.wanderingBoundaryCardContainer}>
              <WanderingBoundaryCard
                name={wanderingBoundary?.name ?? ''}
                description={wanderingBoundary?.description}
                onUnlinkClicked={() => onUnlinkWanderingBoundaryClicked(boundaryId)}
                viewingMode={viewingMode}
              />
              <Divider className={styles.divider} />
            </div>
          );
        })}
      <WanderingBoundaryLinkModal
        isOpen={linkWanderingBoundaryModalIsOpen}
        onClose={closeLinkWanderingBoundaryModal}
        onLinkButtonClicked={onWanderingBoundaryLinkClicked}
        wanderingBoundaryIdsToHide={props.selectedWanderingBoundaryIds ?? []}
      />
    </div>
  );
};

export default WanderingDetectionSettings;
